import * as React from "react"
import { Slot } from "@radix-ui/react-slot"

import { cn } from "@/lib/utils"
import {
  buttonVariants,
  type ButtonVariantProps,
} from "./button-variants"

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    ButtonVariantProps {
  asChild?: boolean
  /** Prevent rapid double clicks by disabling the button briefly after click. */
  preventDoubleClick?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, preventDoubleClick, onClick, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const clicking = React.useRef(false)

    const handleClick: React.MouseEventHandler<HTMLButtonElement> | undefined = (e) => {
      if (preventDoubleClick) {
        if (clicking.current) return
        clicking.current = true
      }

      try {
        const res = onClick?.(e)
        if (preventDoubleClick && res && typeof (res as any).then === "function") {
          ;(res as Promise<any>).finally(() => {
            clicking.current = false
          })
          return
        }
      } finally {
        if (preventDoubleClick) {
          setTimeout(() => (clicking.current = false), 800)
        }
      }
    }

    const finalDisabled = disabled || (preventDoubleClick && clicking.current)

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        disabled={finalDisabled}
        {...(props as any)}
      />
    )
  }
)
Button.displayName = "Button"

export { Button }
